import React, { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Copy, Upload, XCircle, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const BADGE = {
  ok: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", label: "gültig" },
  warn: { cls: "bg-amber-50 text-amber-800 border-amber-200", label: "mit Hinweis" },
  error: { cls: "bg-red-50 text-red-800 border-red-200", label: "Konflikt" },
  duplicate: { cls: "bg-slate-100 text-slate-600 border-slate-200", label: "Duplikat" },
};

export default function AppointmentImportPanel({ coaches = [] }) {
  const [csvText, setCsvText] = useState("");
  const [coachId, setCoachId] = useState("");
  const [preview, setPreview] = useState(null);
  const [acceptWarnings, setAcceptWarnings] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const runPreview = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/appointment-import/preview", { csv_text: csvText, coach_user_id: coachId || null });
      setPreview(data);
      toast.success(`${data.counts.ok} gültig · ${data.counts.warn} mit Hinweis · ${data.counts.error} Konflikte · ${data.counts.duplicate} Duplikate`);
    } catch (e) {
      setPreview(null);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const runCommit = async () => {
    const n = preview.counts.ok + (acceptWarnings ? preview.counts.warn : 0);
    if (!window.confirm(`${n} Termin(e) für ${preview.coach.name} jetzt anlegen? Bestehende Termine werden nicht verändert.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/appointment-import/commit", {
        csv_text: csvText, coach_user_id: preview.coach.user_id, accept_warnings: acceptWarnings,
      });
      setResult(data);
      toast.success(`${data.created_count} Termine angelegt${data.failed_count ? `, ${data.failed_count} fehlgeschlagen` : ""}`);
      runPreview();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 bg-white" data-testid="appt-import-panel">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Terminliste</div>
        <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Termine importieren</div>
        <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
          Liste im Format <span className="font-mono">Datum;Beginn;Ende;Kunde;Coach;UE</span> einfügen. Die Vorschau prüft
          Überschneidungen, bestehende Buchungen, Maßnahmezeiträume, verbleibende UE und die Reihenfolge der Coachingphasen –
          gespeichert wird erst nach deiner Freigabe.
        </p>
      </div>

      <div className="px-5 py-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={coachId} onChange={(e) => { setCoachId(e.target.value); setPreview(null); }}
            className="px-3 py-2 text-xs border border-slate-300 bg-white" data-testid="appt-import-coach"
          >
            <option value="">Coach aus der Liste erkennen</option>
            {coaches.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
          </select>
          <button
            onClick={runPreview} disabled={busy || !csvText.trim()}
            className="px-4 py-2 text-xs bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="appt-import-preview-btn"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} strokeWidth={1.5} />} Importvorschau prüfen
          </button>
        </div>
        <textarea
          value={csvText} onChange={(e) => { setCsvText(e.target.value); setPreview(null); }}
          rows={6} placeholder={"2026-11-02;11:00;13:00;Anna Kibenko;Ana Paula;4\n2026-11-03;09:00;11:00;Mohammad Alizada;Ana Paula;4"}
          className="w-full px-3 py-2 text-xs font-mono border border-slate-300 focus:outline-none focus:border-slate-900"
          data-testid="appt-import-textarea"
        />
      </div>

      {preview && (
        <div className="border-t border-slate-100" data-testid="appt-import-preview">
          <div className="px-5 py-3 flex flex-wrap items-center gap-4 text-xs bg-slate-50">
            <span className="font-medium text-slate-900">{preview.coach.name}</span>
            <span className="text-slate-500">Arbeitszeit {preview.work.start}–{preview.work.end}</span>
            <span className="text-emerald-700">{preview.counts.ok} gültig</span>
            <span className="text-amber-700">{preview.counts.warn} mit Hinweis</span>
            <span className="text-red-700">{preview.counts.error} Konflikte</span>
            <span className="text-slate-500">{preview.counts.duplicate} Duplikate</span>
            <span className="text-slate-900 ml-auto font-medium">{preview.total_ue_import} UE gesamt</span>
          </div>

          {!!preview.per_client.length && (
            <div className="px-5 py-3 border-t border-slate-100 overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-slate-500">
                  <th className="py-1 pr-4 font-medium">Kunde</th><th className="py-1 pr-4 font-medium">Import-UE</th>
                  <th className="py-1 pr-4 font-medium">bereits gebucht</th><th className="py-1 pr-4 font-medium">Kontingent</th>
                  <th className="py-1 font-medium">danach frei</th>
                </tr></thead>
                <tbody>
                  {preview.per_client.map((c) => (
                    <tr key={c.client_id} className="border-t border-slate-100" data-testid={`appt-import-client-${c.client_id}`}>
                      <td className="py-1.5 pr-4 text-slate-900">{c.name}</td>
                      <td className="py-1.5 pr-4">{c.import_ue}</td>
                      <td className="py-1.5 pr-4 text-slate-500">{c.already_booked_ue}</td>
                      <td className="py-1.5 pr-4 text-slate-500">{c.total_ue || "—"}</td>
                      <td className={`py-1.5 ${c.remaining_after !== null && c.remaining_after < 0 ? "text-red-700 font-medium" : "text-slate-700"}`}>
                        {c.remaining_after === null ? "—" : c.remaining_after}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="max-h-[460px] overflow-y-auto border-t border-slate-100 divide-y divide-slate-100">
            {preview.rows.map((r) => {
              const b = BADGE[r.status] || BADGE.error;
              return (
                <div key={r.line} className="px-5 py-2 flex items-start gap-3 text-xs" data-testid={`appt-import-row-${r.line}`}>
                  <span className={`px-2 py-0.5 border ${b.cls} flex-shrink-0`}>{b.label}</span>
                  <span className="font-mono text-slate-700 flex-shrink-0">{r.date} {r.time_from}–{r.time_to}</span>
                  <span className="text-slate-900 flex-1 min-w-[140px]">
                    {r.matched_client || r.client_name}
                    {r.matched_client && r.matched_client !== r.client_name && <span className="text-slate-400"> (Liste: {r.client_name})</span>}
                    {r.section_name && <span className="text-slate-400"> · {r.section_name}</span>}
                  </span>
                  <span className="text-slate-500 flex-shrink-0">{r.ue} UE</span>
                  <span className="flex-1 min-w-[200px] text-right">
                    {(r.issues || []).map((i, k) => <span key={k} className="block text-red-700"><XCircle size={10} className="inline mr-1" />{i}</span>)}
                    {(r.warnings || []).map((i, k) => <span key={k} className="block text-amber-700"><AlertTriangle size={10} className="inline mr-1" />{i}</span>)}
                    {r.status === "ok" && <CheckCircle2 size={12} className="inline text-emerald-600" />}
                  </span>
                </div>
              );
            })}
          </div>

          <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={acceptWarnings} onChange={(e) => setAcceptWarnings(e.target.checked)} data-testid="appt-import-accept-warnings" />
              Termine mit Hinweis ebenfalls anlegen
            </label>
            <button
              onClick={runCommit}
              disabled={busy || !(preview.counts.ok + (acceptWarnings ? preview.counts.warn : 0))}
              className="px-4 py-2 text-xs bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-50 inline-flex items-center gap-2 ml-auto"
              data-testid="appt-import-commit-btn"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} strokeWidth={1.5} />}
              {preview.counts.ok + (acceptWarnings ? preview.counts.warn : 0)} Termine jetzt anlegen
            </button>
          </div>
        </div>
      )}

      {result && (
        <div className="px-5 py-4 border-t border-slate-100 text-xs space-y-1" data-testid="appt-import-result">
          <div className="text-emerald-800">{result.created_count} Termine angelegt.</div>
          {result.failed.map((f) => (
            <div key={f.line} className="text-red-700">Zeile {f.line} · {f.client} · {f.date}: {f.error}</div>
          ))}
        </div>
      )}
    </div>
  );
}
